import { keepPreviousData } from "@tanstack/react-query";
import addDays from "date-fns/addDays";
import format from "date-fns/format";
import formatISO from "date-fns/formatISO";
import startOfToday from "date-fns/startOfToday";
import React, { useCallback, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AutoSizer from "react-virtualized-auto-sizer";

import Typography from "@material-ui/core/Typography";
import useTheme from "@material-ui/core/styles/useTheme";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import Breakpoints from "common/breakpoints";
import routes from "common/routes";
import AppPage from "components/AppPage";
import AppPageContent from "components/AppPageContent";
import AppPageHeader from "components/AppPageHeader";
import AppPageTable from "components/AppPageTable";
import AppPageTableContent from "components/AppPageTableContent";
import ErrorBoundary from "components/ErrorBoundary";
import Loader from "components/Loader";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import ScrollSync from "components/ScrollSync";
import SearchNoResults from "components/SearchNoResults";
import VirtualList from "components/VirtualList";
import { useRacehorse360Api } from "hooks/api";
import { useDefaultTab } from "hooks/useDefaultTab";
import { useInfiniteScroll } from "hooks/useInfiniteScroll";
import WorkoutsPageState from "interfaces/WorkoutsPageState";
import {
  setHorseName,
  setSelectedHorseId,
  setSelectedFacility as setStoreSelectedFacility
} from "store/actions/workoutsPage";
import { getNextDates } from "utils/date-utils";
import {
  getCalendarDay,
  getFacilityBlackoutsCalendar
} from "utils/facilityBlackouts";
import CondensedView from "./CondensedView";
import CondensedViewHeader from "./CondensedViewHeader";
import Header from "./Header";
import HorseWorkoutSchedule from "./HorseWorkoutSchedule";
import { SCHEDULING_ROW_HEIGHT } from "./HorseWorkoutSchedule/styles";

import useStyles from "./styles";

const PAGE_SIZE = 20;
const HORSE_DAYS_COUNT = 35;

const startDate: Date = startOfToday();
const startDateText: string = format(startDate, "MMMM d");
const endDate: Date = addDays(startDate, HORSE_DAYS_COUNT - 1);
const endDateText: string = format(endDate, "MMMM d");
const calendar = getNextDates(startDate, HORSE_DAYS_COUNT).map(getCalendarDay);

const possibleRoutes = [routes.workoutComments, routes.workouts];

const WorkoutsPage = React.memo(() => {
  const { currentUser } = useLoggedInUser();
  const classes = useStyles();

  const dispatch = useDispatch();
  const {
    shouldScrollSync,
    selectedFacility,
    isCondensedView,
    scheduledDatesOnly,
    searchValue
  } = useSelector(
    (state: { workoutsPage: WorkoutsPageState }) => state?.workoutsPage
  );
  const theme = useTheme();

  const [clearSearch, setClearSearch] = useState<boolean>(false);

  const matchesUpMd = useMediaQuery(theme.breakpoints.up("md"));
  const matchesDownSS320 = useMediaQuery(
    theme.breakpoints.down(Breakpoints.SS_320)
  );
  const shouldShowCondensedView = matchesUpMd && isCondensedView;
  const tableBodyRef = useRef<HTMLDivElement>(null);

  const listBlackoutDatesKey = `blackout-${selectedFacility?.id}`;

  const { usePullFacilityBlackouts, useInfiniteListHorses } =
    useRacehorse360Api();

  const pagingOptions = {
    maxResults: PAGE_SIZE,
    includeSummary: true
  };

  const query: racehorse360.IListHorsesRequest = currentUser.isTrainer
    ? {
        query: {
          name: searchValue?.length ? { contains: searchValue } : undefined,
          ...currentUser.trainerHorseAndWorkoutFilter
        },
        getOptions: {
          select: ["name", "activeVetListsCount"],
          orderBy: ["name"]
        },
        pagingOptions: pagingOptions
      }
    : {
        query: {
          horseOrTrainerName: searchValue?.length
            ? { contains: searchValue }
            : undefined,
          ...currentUser.trainerHorseAndWorkoutFilter
        },
        getOptions: {
          select: ["name", "activeVetListsCount"],
          orderBy: ["name"]
        },
        pagingOptions: pagingOptions
      };

  const {
    data: horsesData,
    isLoading: isListHorsesLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage
  } = useInfiniteListHorses(query, {
    placeholderData: keepPreviousData,
    staleTime: 5000
  });

  const horses: racehorse360.IHorse[] = horsesData?.pages?.flat() || [];

  const {
    isLoading: isFacilityBlackoutsLoading,
    data: facilityBlackoutsData = { facilityBlackouts: [] }
  } = usePullFacilityBlackouts(listBlackoutDatesKey, {
    facilityId: selectedFacility?.id,
    startDate: formatISO(addDays(startDate, -30), {
      representation: "date"
    }),
    endDate: formatISO(addDays(endDate, 30), {
      representation: "date"
    })
  });

  const facilityBlackoutsCalendar = getFacilityBlackoutsCalendar(
    facilityBlackoutsData.facilityBlackouts,
    calendar
  );

  const isLoading = [isListHorsesLoading, isFacilityBlackoutsLoading].some(
    Boolean
  );

  const handleFacilityChange = (facility: racehorse360.IFacility) => {
    dispatch(setStoreSelectedFacility(facility));
  };

  const handleResetSearch = useCallback(() => {
    dispatch(setHorseName(""));
    dispatch(setSelectedHorseId(""));
    setClearSearch(true);
  }, [dispatch, searchValue]);

  const renderListHeader = () => (
    <div className={classes.schedulingInfo}>
      <Typography color={"textSecondary"}>
        Scheduling at{" "}
        {matchesDownSS320 ? selectedFacility?.code : selectedFacility?.name}
      </Typography>
      <Typography
        className={classes.schedulingDates}
        color={"textSecondary"}
        align={"right"}
      >
        {startDateText} - {endDateText}
      </Typography>
    </div>
  );

  useInfiniteScroll(
    tableBodyRef,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    true,
    true
  );

  useDefaultTab(possibleRoutes);

  const renderCondensedView = () => {
    return (
      <ScrollSync enabled={!scheduledDatesOnly}>
        <ErrorBoundary>
          <AppPageHeader className={classes.appPageHeader}>
            <Header
              selectedFacility={selectedFacility}
              onFacilityChange={handleFacilityChange}
              clearSearch={clearSearch}
              onClearSearch={setClearSearch}
            >
              <CondensedViewHeader />
            </Header>
          </AppPageHeader>
        </ErrorBoundary>

        <AppPageContent>
          <AppPageTable
            role="table"
            aria-labelledby="tableTitle"
            aria-label="enhanced table"
          >
            <AppPageTableContent
              className={classes.appPageTableContentCondensed}
            >
              {horses?.length ? (
                <CondensedView
                  facilityBlackoutsCalendar={facilityBlackoutsCalendar}
                />
              ) : (
                <>
                  <SearchNoResults
                    value={searchValue}
                    onClearSearch={handleResetSearch}
                  />
                </>
              )}
            </AppPageTableContent>
          </AppPageTable>
        </AppPageContent>
      </ScrollSync>
    );
  };

  const renderStandardView = () => {
    return (
      <>
        <AppPageHeader className={classes.appPageHeader}>
          <ErrorBoundary>
            <Header
              selectedFacility={selectedFacility}
              onFacilityChange={handleFacilityChange}
              clearSearch={clearSearch}
              onClearSearch={setClearSearch}
            />
          </ErrorBoundary>
        </AppPageHeader>

        <AppPageContent>
          <AppPageTable
            role="table"
            aria-labelledby="tableTitle"
            aria-label="enhanced table"
          >
            <AppPageTableContent className={classes.appPageTableContent}>
              <ErrorBoundary>
                <ScrollSync enabled={shouldScrollSync}>
                  <div className={classes.autoSizerContainer}>
                    {horses?.length ? (
                      <AutoSizer>
                        {({ height, width }) => (
                          <>
                            <VirtualList
                              className={classes.virtualListContainer}
                              count={horses?.length}
                              itemHeight={SCHEDULING_ROW_HEIGHT}
                              height={height}
                              width={width}
                              scrollContainerRef={tableBodyRef}
                              itemsBeyond={2}
                              header={renderListHeader()}
                              applyWidthToChild
                            >
                              {horses.map(horse => (
                                <HorseWorkoutSchedule
                                  key={horse.id}
                                  horse={horse}
                                  facilityBlackoutsCalendar={
                                    facilityBlackoutsCalendar
                                  }
                                />
                              ))}
                            </VirtualList>
                          </>
                        )}
                      </AutoSizer>
                    ) : (
                      <>
                        {renderListHeader()}
                        <SearchNoResults
                          value={searchValue}
                          onClearSearch={handleResetSearch}
                        />
                      </>
                    )}
                  </div>
                </ScrollSync>
                {isLoading && <Loader overlay />}
              </ErrorBoundary>
            </AppPageTableContent>
          </AppPageTable>
        </AppPageContent>
      </>
    );
  };

  return (
    <AppPage>
      {shouldShowCondensedView ? renderCondensedView() : renderStandardView()}
    </AppPage>
  );
});

export default WorkoutsPage;
